import { Controller } from 'react-hook-form';
import type { Control, FieldValues, Path, FieldError } from 'react-hook-form';
import type { InputHTMLAttributes, ReactNode } from 'react';
import type { Icon } from '@tabler/icons-react';

interface FormFieldProps<T extends FieldValues> extends Omit<InputHTMLAttributes<HTMLInputElement>, 'name'> {
  control: Control<T>;
  name: Path<T>;
  label: ReactNode;
  error?: FieldError;
  hint?: string;
  required?: boolean;
  wrapperClassName?: string;
  inputClassName?: string;
  labelClassName?: string;
  icon?: Icon;
  valueAsNumber?: boolean;
}

export function FormField<T extends FieldValues>({
  control,
  name,
  label,
  error,
  hint,
  required,
  wrapperClassName = 'mb-3',
  inputClassName = '',
  labelClassName = '',
  icon: Icon,
  valueAsNumber = false,
  ...inputProps
}: FormFieldProps<T>) {
  return (
    <div className={wrapperClassName}>
      <label htmlFor={name} className={`form-label ${required ? 'required' : ''} ${labelClassName}`}>
        {label}
      </label>
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value, ...field } }) => {
          const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            onChange(valueAsNumber ? (e.target.value === '' ? undefined : Number(e.target.value)) : e.target.value);
          };

          return Icon ? (
            <div className="input-icon">
              <span className="input-icon-addon">
                <Icon size={18} />
              </span>
              <input
                {...field}
                value={value ?? ''}
                onChange={handleChange}
                id={name}
                className={`form-control ${error ? 'is-invalid' : ''} ${inputClassName}`}
                {...inputProps}
              />
            </div>
          ) : (
            <input
              {...field}
              value={value ?? ''}
              onChange={handleChange}
              id={name}
              className={`form-control ${error ? 'is-invalid' : ''} ${inputClassName}`}
              {...inputProps}
            />
          );
        }}
      />
      {hint && <small className="form-hint">{hint}</small>}
      {error?.message && <div className="invalid-feedback d-block">{error.message}</div>}
    </div>
  );
}
